import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  X, 
  Copy, 
  Key, 
  Eye, 
  EyeOff,
  Mail,
  Globe,
  Calendar,
  CreditCard,
  Clock,
  BookOpen,
  CheckCircle,
  User,
  FileText,
  Pencil,
  Save,
  XCircle,
  Send,
  Inbox
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatLastActive, getDaysUntilExpiration } from '@/services/clientService';

// Default test account password for test plans
const DEFAULT_TEST_PASSWORD = 'Aa123456!!!';

const PLAN_OPTIONS = [
  { value: 'Freemium', label: 'Freemium', price: 0 },
  { value: 'Monthly', label: 'Monthly', price: 4.99 },
  { value: 'Lifetime', label: 'Lifetime', price: 37 },
  { value: 'test_lifetime', label: 'Test Lifetime', price: 37 },
  { value: 'test_monthly', label: 'Test Monthly', price: 4.99 },
];

interface CRMClient {
  id: string;
  user_id: string | null;
  name: string;
  email: string;
  country: string | null;
  plan: string;
  plan_price: number;
  amount_paid: number | null;
  status: string;
  paid: boolean;
  has_access: boolean | null;
  subscription_start: string;
  subscription_end: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: string | null;
  motivation_letter: string | null;
  applied_at: string | null;
  last_active_at: string | null;
  created_at: string;
  updated_at: string;
  billing_name: string | null;
  billing_address: string | null;
}

interface AcademyProgress {
  academy_id: string;
  academy_name: string;
  completed_modules: number;
  total_modules: number;
}

interface ClassCompletion {
  class_id: string;
  score: number;
  total_questions: number;
  passed: boolean;
  completed_at: string;
}

interface ClientEmail {
  id: string;
  email_type: string;
  subject: string;
  sent_at: string;
  status: string;
}

interface ClientInfoModalProps {
  client: CRMClient;
  onClose: () => void;
  onClientUpdated?: () => void;
}

export const ClientInfoModal = ({ client, onClose, onClientUpdated }: ClientInfoModalProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: client.name,
    email: client.email,
    plan: client.plan,
    paid: client.paid,
    has_access: client.has_access ?? false,
    status: client.status,
  });
  const [expandedData, setExpandedData] = useState<{
    academyProgress: AcademyProgress[];
    classCompletions: ClassCompletion[];
    emails: ClientEmail[];
    loading: boolean;
  }>({ academyProgress: [], classCompletions: [], emails: [], loading: true });

  useEffect(() => {
    loadClientData();
  }, [client.id]);

  useEffect(() => {
    // Reset edit form when client changes
    setEditForm({
      name: client.name,
      email: client.email,
      plan: client.plan,
      paid: client.paid,
      has_access: client.has_access ?? false,
      status: client.status,
    });
    setIsEditing(false);
  }, [client]);

  const loadClientData = async () => {
    try {
      const [progressRes, completionsRes, emailsRes] = await Promise.all([
        supabase
          .from('client_academy_progress')
          .select('academy_id, academy_name, completed_modules, total_modules')
          .eq('client_id', client.id),
        supabase
          .from('class_completions')
          .select('class_id, score, total_questions, passed, completed_at')
          .eq('client_id', client.id)
          .order('completed_at', { ascending: false })
          .limit(20),
        supabase
          .from('client_emails')
          .select('id, email_type, subject, sent_at, status')
          .eq('client_id', client.id)
          .order('sent_at', { ascending: false })
          .limit(50)
      ]);

      setExpandedData({
        academyProgress: progressRes.data || [],
        classCompletions: completionsRes.data || [],
        emails: emailsRes.data || [],
        loading: false
      });
    } catch (error) {
      console.error('Error loading client data:', error);
      setExpandedData({ academyProgress: [], classCompletions: [], emails: [], loading: false });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Check if client is a test account (by plan type)
  const isTestAccount = (plan: string) => {
    const planLower = plan?.toLowerCase() || '';
    return planLower.startsWith('test_') || planLower.startsWith('test-') || planLower === 'test';
  };

  const getTestPassword = () => {
    return DEFAULT_TEST_PASSWORD;
  };

  const getStatusLabel = () => {
    if (!client.paid) return 'Pending Payment';
    const days = getDaysUntilExpiration(client.subscription_end);
    if (days < 0) return 'Expired';
    if (days <= 30) return 'Expiring Soon';
    return 'Active';
  };

  const getStatusColor = () => {
    if (!client.paid) return 'bg-yellow-500/20 text-yellow-400';
    const days = getDaysUntilExpiration(client.subscription_end);
    if (days < 0) return 'bg-red-500/20 text-red-400';
    if (days <= 30) return 'bg-orange-500/20 text-orange-400';
    return 'bg-green-500/20 text-green-400';
  };

  const getClientTypeLabel = () => {
    const plan = client.plan?.toLowerCase() || '';
    if (plan === 'test_lifetime' || plan === 'test-lifetime') return 'Test Client Admin';
    if (plan === 'test_monthly' || plan === 'test-monthly') return 'Test Monthly Admin';
    if (plan === 'admin') return 'Admin';
    if (!client.paid) return 'Freemium';
    if (plan === 'lifetime') return 'Lifetime';
    if (plan === 'monthly') return 'Monthly';
    return plan || 'Freemium';
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const selectedPlan = PLAN_OPTIONS.find(p => p.value === editForm.plan);
      const planPrice = selectedPlan?.price ?? client.plan_price;
      
      // Determine paid status and access based on plan
      const isPaid = editForm.plan !== 'Freemium';
      const hasAccess = isPaid;
      const status = isPaid ? 'active' : 'inactive';
      
      const { error } = await supabase
        .from('clients')
        .update({
          name: editForm.name,
          email: editForm.email,
          plan: editForm.plan,
          plan_price: planPrice,
          paid: isPaid,
          has_access: hasAccess,
          status: status,
        })
        .eq('id', client.id);

      if (error) throw error;

      toast.success('Client updated successfully');
      setIsEditing(false);
      onClientUpdated?.();
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Failed to update client');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      name: client.name,
      email: client.email,
      plan: client.plan,
      paid: client.paid,
      has_access: client.has_access ?? false,
      status: client.status,
    });
    setIsEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-4xl bg-card border border-border/30 rounded-xl shadow-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/20">
          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name" className="text-xs text-muted-foreground">Name</Label>
                    <Input
                      id="edit-name"
                      value={editForm.name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      className="h-9 bg-background/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email" className="text-xs text-muted-foreground">Email</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                      className="h-9 bg-background/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-plan" className="text-xs text-muted-foreground">Plan</Label>
                    <Select
                      value={editForm.plan}
                      onValueChange={(value) => setEditForm(prev => ({ ...prev, plan: value }))}
                    >
                      <SelectTrigger className="h-9 bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border border-border z-[100]">
                        {PLAN_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label} (${option.price})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-light text-foreground">{client.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">{client.email}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor()}`}>
                    {getStatusLabel()}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                    {getClientTypeLabel()}
                  </span>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 ml-4">
            {isEditing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Account & Auth */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
                <User className="w-4 h-4" /> Account & Auth
              </h4>
              <div className="space-y-3 p-4 bg-muted/10 rounded-lg border border-border/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Client ID</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-foreground">{client.id.slice(0, 8)}...</code>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(client.id)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">User ID</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-foreground">{client.user_id ? `${client.user_id.slice(0, 8)}...` : 'N/A'}</code>
                    {client.user_id && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(client.user_id!)}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Email</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground">{client.email}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(client.email)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Has Access</span>
                  <span className={`text-xs ${client.has_access ? 'text-green-400' : 'text-red-400'}`}>
                    {client.has_access ? 'Yes' : 'No'}
                  </span>
                </div>
                
                {/* Test Account Password - Shows for test plan types */}
                {isTestAccount(client.plan) && (
                  <div className="pt-3 border-t border-border/20">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-primary flex items-center gap-1">
                        <Key className="w-3 h-3" /> Test Password
                      </span>
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-mono text-primary">
                          {showPassword ? getTestPassword() : '••••••••••'}
                        </code>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-primary"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-primary"
                          onClick={() => copyToClipboard(getTestPassword())}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Dates & Activity */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Dates & Activity
              </h4>
              <div className="space-y-3 p-4 bg-muted/10 rounded-lg border border-border/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Created At</span>
                  <span className="text-xs text-foreground">{new Date(client.created_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Applied At</span>
                  <span className="text-xs text-foreground">{client.applied_at ? new Date(client.applied_at).toLocaleString() : 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Subscription Start</span>
                  <span className="text-xs text-foreground">{client.subscription_start}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Subscription End</span>
                  <span className="text-xs text-foreground">{client.subscription_end}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Last Active</span>
                  <span className="text-xs text-foreground">{formatLastActive(client.last_active_at)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Updated At</span>
                  <span className="text-xs text-foreground">{new Date(client.updated_at).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Payment & Stripe */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Payment & Stripe
              </h4>
              <div className="space-y-3 p-4 bg-muted/10 rounded-lg border border-border/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Plan</span>
                  <span className="text-xs text-foreground">{client.plan}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Plan Price</span>
                  <span className="text-xs text-foreground">${client.plan_price}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Amount Paid</span>
                  <span className={`text-xs font-medium ${client.amount_paid != null ? 'text-green-400' : 'text-muted-foreground'}`}>
                    {client.amount_paid != null ? `$${client.amount_paid.toFixed(2)}` : 'N/A'}
                    {client.amount_paid != null && client.amount_paid < client.plan_price && (
                      <span className="ml-1 text-yellow-400">(discount)</span>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Paid</span>
                  <span className={`text-xs ${client.paid ? 'text-green-400' : 'text-red-400'}`}>
                    {client.paid ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Subscription Status</span>
                  <span className="text-xs text-foreground">{client.subscription_status || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Stripe Customer</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-foreground">{client.stripe_customer_id ? `${client.stripe_customer_id.slice(0, 12)}...` : 'N/A'}</code>
                    {client.stripe_customer_id && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(client.stripe_customer_id!)}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Stripe Sub ID</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-foreground">{client.stripe_subscription_id ? `${client.stripe_subscription_id.slice(0, 12)}...` : 'N/A'}</code>
                    {client.stripe_subscription_id && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(client.stripe_subscription_id!)}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Billing Name</span>
                  <span className="text-xs text-foreground">{client.billing_name || 'N/A'}</span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-xs text-muted-foreground">Billing Address</span>
                  <span className="text-xs text-foreground text-right max-w-[200px]">{client.billing_address || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Application Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
                <FileText className="w-4 h-4" /> Application Info
              </h4>
              <div className="space-y-3 p-4 bg-muted/10 rounded-lg border border-border/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Country</span>
                  <span className="text-xs text-foreground flex items-center gap-1">
                    <Globe className="w-3 h-3" /> {client.country || 'N/A'}
                  </span>
                </div>
                {client.motivation_letter && (
                  <div className="pt-3 border-t border-border/20">
                    <span className="text-xs text-muted-foreground block mb-2">Motivation Letter</span>
                    <p className="text-xs text-foreground whitespace-pre-wrap bg-background/50 p-3 rounded border border-border/20 max-h-32 overflow-y-auto">
                      {client.motivation_letter}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Academy Progress */}
          <div className="mt-6 space-y-4">
            <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Academy Progress
            </h4>
            <div className="p-4 bg-muted/10 rounded-lg border border-border/20">
              {expandedData.loading ? (
                <p className="text-xs text-muted-foreground">Loading progress...</p>
              ) : expandedData.academyProgress.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {expandedData.academyProgress.map((progress) => (
                    <div key={progress.academy_id} className="p-3 bg-background/50 rounded border border-border/20">
                      <p className="text-xs font-medium text-foreground mb-2">{progress.academy_name}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted/30 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${(progress.completed_modules / progress.total_modules) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {progress.completed_modules}/{progress.total_modules}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No academy progress yet</p>
              )}
            </div>
          </div>

          {/* Recent Class Completions */}
          <div className="mt-6 space-y-4">
            <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Recent Class Completions
            </h4>
            <div className="p-4 bg-muted/10 rounded-lg border border-border/20">
              {expandedData.loading ? (
                <p className="text-xs text-muted-foreground">Loading completions...</p>
              ) : expandedData.classCompletions.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {expandedData.classCompletions.map((completion, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-background/50 rounded border border-border/20">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${completion.passed ? 'bg-green-400' : 'bg-red-400'}`} />
                        <span className="text-xs text-foreground">
                          Score: {completion.score}/{completion.total_questions}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(completion.completed_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No class completions yet</p>
              )}
            </div>
          </div>

          {/* Email History */}
          <div className="mt-6 space-y-4">
            <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
              <Inbox className="w-4 h-4" /> Email History
            </h4>
            <div className="p-4 bg-muted/10 rounded-lg border border-border/20">
              {expandedData.loading ? (
                <p className="text-xs text-muted-foreground">Loading emails...</p>
              ) : expandedData.emails.length > 0 ? (
                <ScrollArea className="max-h-64">
                  <div className="space-y-2">
                    {expandedData.emails.map((email) => (
                      <div key={email.id} className="flex items-start justify-between p-3 bg-background/50 rounded border border-border/20">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-primary/10 rounded">
                            <Send className="w-3 h-3 text-primary" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-foreground">{email.subject}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary capitalize">
                                {email.email_type.replace('_', ' ')}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                email.status === 'sent' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {email.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(email.sent_at).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-6">
                  <Inbox className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No emails sent to this client yet</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    Use Quick Actions to send emails
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ClientInfoModal;
